import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { AzureUserInfo, useTokenRefresh } from '@/auth/azure/azureManager'
import { Roles } from '@/auth/azure/Roles'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'
import { gridTotalItemsLabel } from '@/constants'
import { analisi, personalizzazione, user } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { columnSourceFactory, isUserRoleAllowed, onDataGridSelectedRowButtonClick } from '@/routes/utils'
import { useScreenSize } from '@/themes/media-query'
import DataGrid, {
  Button as GridButton,
  Column,
  ColumnChooser,
  DataGridRef,
  DataGridTypes,
  Editing,
  Export,
  FilterPanel,
  FilterRow,
  HeaderFilter,
  Item,
  Pager,
  Scrolling,
  Search,
  SearchPanel,
  Selection,
  Sorting,
  StateStoring,
  Toolbar,
} from 'devextreme-react/data-grid'
import { EdmLiteral, ODataStore } from 'devextreme/common/data'
import { exportDataGrid } from 'devextreme/excel_exporter'
import { ClickEvent } from 'devextreme/ui/button'
import dxDataGrid, { ColumnButtonClickEvent, ExportingEvent, RowDblClickEvent } from 'devextreme/ui/data_grid'
import notify from 'devextreme/ui/notify'
import { Workbook } from 'exceljs'
import saveAs from 'file-saver'
import { memo, useCallback, useRef } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { confirm } from 'devextreme/ui/dialog'

type CustomizationsGridProps = { currentFilter?: any[]; userInfo?: AzureUserInfo | undefined }

const CustomizationsGrid = (props: CustomizationsGridProps) => {
  const token = useTokenRefresh()
  const currentScreenSize = useScreenSize()
  const navigate = useNavigate()
  const { clientId } = useParams()
  const gridRef = useRef<DataGridRef>(null)
  const { service } = useQsAdminApiManager()

  const calculateResponsabiliValue = (rowData: personalizzazione) => {
    let responsabili: string = ''
    rowData.responsabili?.map(
      (responsabile: user) => (responsabili = `${responsabili + responsabile.fullname?.toUpperCase()} `),
    )
    return responsabili.trim()
  }

  const calculateAnalisiValue = (rowData: personalizzazione) => {
    let analisi: string = ''
    rowData.analisis?.map((an: analisi) => (analisi = `${analisi + an.ded_Dis} `))
    return analisi.trim()
  }

  const cellResponsabiliRender = (cellData: DataGridTypes.ColumnCellTemplateData) => {
    return (
      <>
        {cellData.data.responsabili?.map((resp: user, index: number) => {
          return (
            <div className={`dx-tag-content custom-tag-padding`} key={`ctc-${index}`}>
              <span>{resp.fullname}</span>
            </div>
          )
        })}
      </>
    )
  }

  const cellAnalisiRender = (cellData: DataGridTypes.ColumnCellTemplateData) => {
    const data: personalizzazione = cellData.data
    if (data.analisis?.length === 0) {
      return <></>
    }
    if (data.analisis?.length === 1) {
      return (
        <div>
          <Link to={`/analysis/${data.analisis?.[0].id}`}>{data.analisis?.[0].ded_Dis}</Link>
        </div>
      )
    }
    return (
      <>
        {data.analisis?.map((an: analisi, index: number) => {
          return (
            <div className={`dx-tag-content custom-tag-padding`} key={`ctc-${index}`}>
              <span>
                <Link to={`/analysis/${an.id}`}>{an.ded_Dis}</Link>
              </span>
            </div>
          )
        })}
      </>
    )
  }

  const calculateResponsabiliFilterExpression = (
    filterValue: any,
    selectedFilterOperation: string | null,
    target: string,
  ): string | (() => any) | Array<any> => {
    console.log('FLT', filterValue, selectedFilterOperation, target)

    if (target === 'filterBuilder' || target === 'filterRow') {
      switch (selectedFilterOperation) {
        case '=': {
          return [[new EdmLiteral(`responsabili/any(a: a/fullname eq '${filterValue}')`), '=', true]]
        }
        case '<>': {
          return [[new EdmLiteral(`responsabili/any(a: a/fullname eq '${filterValue}')`), '=', false]]
        }
        case 'contains':
        case 'startswith':
        case 'endswith': {
          return [
            [
              new EdmLiteral(`responsabili/any(a: ${selectedFilterOperation}(a/fullname, '${filterValue}'))`),
              '=',
              true,
            ],
          ]
        }
        default: {
          // caso notcontains
          return [[new EdmLiteral(`responsabili/any(a: contains(a/nome, '${filterValue}'))`), '=', false]]
        }
      }
    } else if (target === 'search') {
      return [[new EdmLiteral(`responsabili/any(a: contains(a/fullname, '${filterValue}'))`), '=', true]]
    }

    return filterValue
  }

  const calculateAnalisiFilterExpression = (
    filterValue: any,
    selectedFilterOperation: string | null,
    target: string,
  ): string | (() => any) | Array<any> => {
    console.log('FLT', filterValue, selectedFilterOperation, target)

    if (target === 'filterBuilder' || target === 'filterRow') {
      switch (selectedFilterOperation) {
        case '=': {
          return [[new EdmLiteral(`analisi/any(a: a/ded_Dis eq '${filterValue}')`), '=', true]]
        }
        case '<>': {
          return [[new EdmLiteral(`analisi/any(a: a/ded_Dis eq '${filterValue}')`), '=', false]]
        }
        case 'contains':
        case 'startswith':
        case 'endswith': {
          return [
            [new EdmLiteral(`analisi/any(a: ${selectedFilterOperation}(a/ded_Dis, '${filterValue}'))`), '=', true],
          ]
        }
        default: {
          // caso notcontains
          return [[new EdmLiteral(`analisi/any(a: contains(a/ded_Dis, '${filterValue}'))`), '=', false]]
        }
      }
    } else if (target === 'search') {
      return [[new EdmLiteral(`analisi/any(a: contains(a/ded_Dis, '${filterValue}'))`), '=', true]]
    }

    return filterValue
  }

  const getGridHeight = useCallback(() => {
    let h = '72vh'
    if (currentScreenSize.isMedium) {
      h = '68vh'
    } else if (currentScreenSize.isSmall) {
      h = '120vh'
    } else if (currentScreenSize.isXSmall) {
      h = '65vh'
    }
    return h
  }, [currentScreenSize])

  const onExporting = (e: ExportingEvent) => {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet('Customizations')
    const component: dxDataGrid = e.component
    exportDataGrid({ component, worksheet }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Customizations.xlsx')
      })
    })
  }

  const deletePers = async (persId: number) => {
    return await service.personalizzazione(persId).delete()
  }

  const onDeleteClick = (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(gridRef, (rowdata: personalizzazione) => {
      const result = confirm(
        `<i>Vuoi davvero eliminare la personalizzazione <b>${rowdata.ded_Dis}</b>?</i>`,
        'Eliminazione personalizzazione',
      )
      result.then((dialogResult) => {
        if (dialogResult === false) return
        deletePers(rowdata.id)
          .then(() => {
            // Aggiornamento griglia
            gridRef.current?.instance().refresh()

            notify(
              {
                message: `Personalizzazione '${rowdata['ded_Dis']}' del cliente ${
                  rowdata['sede']?.azienda?.nome ?? 'Non definito'
                } eliminata con successo`,
                type: 'success',
                displayTime: 5000,
              },
              {
                position: 'bottom center',
                direction: 'up-push',
              },
            )
          })
          .catch((error: unknown) => {
            notify(
              {
                message: `Errore eliminazione personalizzazione: ${error}`,
                type: 'error',
                displayTime: 5000,
              },
              {
                position: 'bottom center',
                direction: 'up-push',
              },
            )
          })
      })
    })
  }

  const customizationsDataSource = {
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/personalizzazione`,
      key: 'id',
      keyType: 'Int32',
      version: 4,
      errorHandler: (e) => {
        console.error(e.errorDetails)
        if (!e.errorDetails) return
        notify(
          {
            message: `Errore : ${e.errorDetails?.message}`,
            type: 'error',
            displayTime: 5000,
          },
          {
            position: 'bottom center',
            direction: 'up-push',
          },
        )
      },
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    expand: ['analisis', 'sede($expand=azienda($expand=agente($expand=commerciale_qs),rivenditore))', 'responsabili'],
    filter: props.currentFilter?.length && props.currentFilter.length > 0 ? props.currentFilter : null,
    requireTotalCount: true,
  }

  return (
    <>
      <DataGrid
        id={clientId ? 'client-customizations-datagrid' : 'customizations-datagrid'}
        height={getGridHeight()}
        dataSource={customizationsDataSource}
        noDataText="Nessuna personalizzazione trovata"
        className={'grid dx-card wide-card'}
        showBorders={false}
        showColumnLines={true}
        wordWrapEnabled={false}
        showRowLines={true}
        focusedRowEnabled={true}
        rowAlternationEnabled={true}
        allowColumnResizing={true}
        allowColumnReordering={true}
        width="100%"
        onRowDblClick={(e: RowDblClickEvent<any, any>) => {
          if (e.rowType === 'data') navigate(`${e.data.id}`)
        }}
        onExporting={onExporting}
        ref={gridRef}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
        remoteOperations={{
          filtering: true,
          grouping: true,
          groupPaging: true,
          paging: true,
          sorting: true,
          summary: false,
        }}
        grouping={{
          // Fondamentale per il raggruppamento veloce!!!
          autoExpandAll: true,
        }}
        groupPanel={{
          visible: currentScreenSize.isLarge || currentScreenSize.isMedium,
          emptyPanelText: 'Trascina qui una colonna per raggruppare',
        }}
      >
        <Pager
          visible={true}
          allowedPageSizes={'auto'}
          displayMode={'compact'}
          showPageSizeSelector={false}
          showInfo={true}
          showNavigationButtons={false}
          infoText={gridTotalItemsLabel}
        />
        <FilterRow visible={true} />
        <SearchPanel visible={true} width={currentScreenSize.isXSmall ? 180 : 250} />
        <Sorting mode="multiple"></Sorting>
        <FilterPanel visible={true} />
        <Export enabled={true} />
        <Editing allowAdding={true} />
        <HeaderFilter visible={true} />
        <Selection mode="single" />
        <ColumnChooser enabled={true} />
        <Scrolling mode="virtual" />
        <StateStoring
          enabled={true}
          type="localStorage"
          storageKey={clientId ? 'client-customizations-datagrid' : 'customizations-datagrid'}
          savingTimeout={50}
        />
        <Column
          visible={false}
          type="buttons"
          width={currentScreenSize.isXSmall || currentScreenSize.isSmall ? '10%' : '2%'}
          alignment="left"
          showInColumnChooser={false}
        >
          <GridButton
            hint="Details"
            icon="search"
            onClick={(e: ColumnButtonClickEvent) => {
              navigate(`${e.row?.data.id}`)
            }}
          ></GridButton>
        </Column>
        <Column
          dataField={'id'}
          caption="ID"
          visible={false}
          allowSearch={false}
          dataType="number"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 1 : undefined
          }
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'personalizzazione', 'id', true)}></HeaderFilter>
          )}
        </Column>
        <Column
          dataField="ded_Dis"
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: personalizzazione = cellData.row.data
            return <ObjectCodeLink url={`${data.id}`} text={data.ded_Dis}></ObjectCodeLink>
          }}
          visible={true}
          caption="CODICE"
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 95 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 95 : undefined}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 15 : undefined
          }
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'personalizzazione', 'ded_Dis', true)}>
              <Search enabled={true} searchExpr={'ded_Dis'} />
            </HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'titolo'}
          caption="TITOLO"
          visible={true}
          allowSearch={false}
          dataType="string"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 14 : undefined
          }
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'personalizzazione', 'titolo', true)}>
              <Search enabled={true} searchExpr={'ded_Dis'} />
            </HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'sede.azienda.nome'}
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: personalizzazione = cellData.row.data
            return (
              <ObjectCodeLink
                url={`/clients/${data.sede?.azienda?.id}`}
                text={data.sede?.azienda?.nome}
              ></ObjectCodeLink>
            )
          }}
          caption={'AZIENDA'}
          visible={clientId === undefined}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 13 : undefined
          }
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'azienda', 'nome')}>
              <Search enabled={true} searchExpr={'nome'} />
            </HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'sede.nome'}
          caption={'SEDE'}
          allowHeaderFiltering={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 12 : undefined
          }
        />
        <Column
          name={'info_responsabili'}
          caption={'RESPONSABILI'}
          calculateFilterExpression={calculateResponsabiliFilterExpression}
          calculateCellValue={calculateResponsabiliValue}
          cellRender={cellResponsabiliRender}
          allowFiltering={true}
          allowHeaderFiltering={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 11 : undefined
          }
        />
        <Column
          visible={false}
          dataField={'note'}
          caption={'NOTE'}
          allowFiltering={true}
          allowHeaderFiltering={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 10 : undefined
          }
        ></Column>
        <Column
          name={'info_analisi'}
          caption={'ANALISI'}
          calculateFilterExpression={calculateAnalisiFilterExpression}
          calculateCellValue={calculateAnalisiValue}
          cellRender={cellAnalisiRender}
          allowFiltering={true}
          allowHeaderFiltering={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 9 : undefined
          }
        />
        <Column
          dataField={'supporto_sviluppo'}
          caption="NOTIFICA"
          dataType="boolean"
          hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 8 : undefined}
          visible={true}
        ></Column>
        <Toolbar>
          <Item
            location="before"
            locateInMenu="never"
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Refresh',
              text: 'Refresh',
              icon: 'refresh',
              onClick: (e: ClickEvent) => {
                gridRef.current?.instance().refresh()
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item name="groupPanel" />
          <Item
            visible={isUserRoleAllowed(props.userInfo?.roles, [
              Roles.GlobalAdministrator,
              Roles.TechDeveloper,
              Roles.Supervisor,
              Roles.Administrator,
            ])}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            name="addRowButton"
            options={{
              onClick: () => {
                navigate(`new`)
              },
              hint: 'Nuova personalizzazione',
              text: 'Nuova personalizzazione',
            }}
          />
          <Item
            visible={isUserRoleAllowed(props.userInfo?.roles, [
              Roles.GlobalAdministrator,
              Roles.TechDeveloper,
              Roles.Supervisor,
              Roles.Administrator,
            ])}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Elimina personalizzazione',
              text: 'Elimina personalizzazione',
              icon: 'trash',
              onClick: onDeleteClick,
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            name="columnChooserButton"
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            options={{ hint: 'Scelta colonne', text: 'Scelta colonne' }}
          />
          <Item
            name="exportButton"
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            options={{ hint: 'Esporta dati in excel' }}
          />
          <Item name="searchPanel" />
        </Toolbar>
      </DataGrid>
    </>
  )
}

const CustomizationsGridMemoed = memo(CustomizationsGrid, (oldProps, newProps) => {
  console.log(oldProps, newProps)
  return oldProps.currentFilter === newProps.currentFilter
})

export default CustomizationsGridMemoed
